// Créances clients : balance âgée, factures dues par client, relances internes et prochaines actions.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { AccountBalanceWalletOutlined, ExpandMore, ExpandLess, FileDownloadOutlined, PhoneInTalkOutlined, ScheduleOutlined, WarningAmberOutlined } from '@mui/icons-material';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { comptaAPI } from '../../services/api';
import { apiError, dateFr, today } from '../../utils/format';
import { ecrireExcel } from '../../utils/excel';
import { tokens } from '../../theme';

const F = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} F`;
const TRANCHES = [['NON_ECHU', 'Non échu', '#5B6675'], ['J0_30', '1 à 30 j', '#C9850F'], ['J31_60', '31 à 60 j', '#D46B08'], ['J61_90', '61 à 90 j', '#B4232A'], ['PLUS_90', '+ de 90 j', '#7A1015']];
const TYPES = { APPEL: 'Appel', EMAIL: 'Email', COURRIER: 'Courrier', VISITE: 'Visite', NOTE: 'Note' };
const couleur = (r) => (r <= 0 ? 'text.secondary' : r <= 30 ? 'warning.main' : 'error.main');

function RelanceDialog({ facture, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [hist, setHist] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!facture) return;
    setError(''); setF({ type: 'APPEL', date: today(), note: '', prochaine_action: '' });
    comptaAPI.relances(facture.source === 'ERP' ? { invoice_id: facture.id } : { facture_ext_id: facture.id }).then((r) => setHist(r.data)).catch(() => setHist([]));
  }, [facture]);
  const save = async () => {
    setError('');
    try {
      await comptaAPI.relancer({ ...f, ...(facture.source === 'ERP' ? { invoice_id: facture.id } : { facture_ext_id: facture.id }) });
      onSaved('Relance enregistrée');
    } catch (e) { setError(apiError(e)); }
  };
  return (
    <Dialog open={!!facture} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Relance — {facture?.numero} ({facture?.client})</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {facture && f && (
          <>
            <Typography sx={{ fontSize: 14, mb: 2 }}>Reste dû <b>{F(facture.reste)}</b> · échéance {dateFr(facture.date_echeance)}{facture.retard > 0 ? ` · ${facture.retard} j de retard` : ''}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField select fullWidth label="Type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{Object.entries(TYPES).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}</TextField></Grid>
              <Grid item xs={6}><TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Compte rendu" placeholder="Interlocuteur, réponse, date de paiement annoncée…" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth type="date" label="Prochaine action le" InputLabelProps={{ shrink: true }} value={f.prochaine_action} onChange={(e) => setF({ ...f, prochaine_action: e.target.value })}
                helperText="Un rappel vous sera envoyé ce jour-là (cloche)" /></Grid>
            </Grid>
            {hist.length > 0 && (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 2, mb: 1 }}>Historique</Typography>
                {hist.map((h) => (
                  <Box key={h.id} sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{dateFr(h.date)} · {TYPES[h.type]} · {h.auteur}{h.prochaine_action ? ` · prochaine action le ${dateFr(h.prochaine_action)}` : ''}</Typography>
                    {h.note && <Typography sx={{ fontSize: 14 }}>{h.note}</Typography>}
                  </Box>
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Annuler</Button><Button variant="contained" onClick={save}>Enregistrer</Button></DialogActions>
    </Dialog>
  );
}

const Creances = () => {
  const [d, setD] = useState(null);
  const [vue, setVue] = useState('clients');
  const [ouverts, setOuverts] = useState({});
  const [relance, setRelance] = useState(null);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const charger = useCallback(() => comptaAPI.creances().then((r) => setD(r.data)).catch((e) => setError(apiError(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const b = d?.balance;

  const ligneFacture = (f, client = true) => (
    <TableRow key={`${f.source}-${f.id}`} hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><b>{f.numero}</b>{f.source === 'REPRISE' && <Chip size="small" variant="outlined" label="avant ERP" sx={{ ml: 0.5 }} />}
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{f.marche_numero ? `BC ${f.marche_numero}` : ''}</Typography></TableCell>
      {client && <TableCell>{f.client}</TableCell>}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{f.date_facture ? dateFr(f.date_facture) : '—'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{f.date_echeance ? dateFr(f.date_echeance) : '—'}<Typography sx={{ fontSize: 12, color: couleur(f.retard), fontWeight: f.retard > 0 ? 700 : 400 }}>{f.retard > 0 ? `${f.retard} j de retard` : f.retard === 0 ? "aujourd'hui" : `dans ${-f.retard} j`}</Typography></TableCell>
      <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 700 }}>{F(f.reste)}{f.reste !== f.montant && <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontFamily: tokens.mono }}>sur {F(f.montant)}</Typography>}</TableCell>
      <TableCell sx={{ fontSize: 12.5 }}>{f.derniere_relance ? `${TYPES[f.derniere_relance_type]} le ${dateFr(f.derniere_relance)}` : '—'}
        {f.prochaine_action && <Typography sx={{ fontSize: 12, color: f.prochaine_action <= today() ? 'error.main' : 'text.secondary', fontWeight: f.prochaine_action <= today() ? 700 : 400 }}>Action le {dateFr(f.prochaine_action)}</Typography>}</TableCell>
      <TableCell align="right"><Button size="small" startIcon={<PhoneInTalkOutlined />} onClick={() => setRelance(f)}>Relancer</Button></TableCell>
    </TableRow>
  );
  const exporter = async () => {
    const lignes = [['Client', 'Facture', 'Origine', 'BC', 'Date', 'Échéance', 'Retard (j)', 'Montant TTC', 'Reste dû', 'Dernière relance', 'Prochaine action'],
      ...d.factures.map((f) => [f.client, f.numero, f.source === 'ERP' ? 'ERP' : 'Avant ERP', f.marche_numero || '', f.date_facture ? new Date(`${f.date_facture}T00:00:00Z`) : null,
        f.date_echeance ? new Date(`${f.date_echeance}T00:00:00Z`) : null, f.retard, f.montant, f.reste, f.derniere_relance || '', f.prochaine_action || ''])];
    await ecrireExcel(lignes, `creances-${d.date}.xlsx`, { largeurs: [24, 16, 10, 14, 12, 12, 10, 14, 14, 14, 14] });
  };

  return (
    <Layout title="Créances clients" plain actions={d && <Button startIcon={<FileDownloadOutlined />} onClick={() => exporter().catch((e) => setError(e.message))}>Excel</Button>}>
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {b && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><Kpi compact icon={AccountBalanceWalletOutlined} tint="navy" label="À encaisser" value={F(b.total)} valueSize={22} sub={`${b.nb} facture(s)`} /></Grid>
            <Grid item xs={6} md={3}><Kpi compact icon={WarningAmberOutlined} tint={b.echu ? 'red' : 'gray'} label="Échu" value={F(b.echu)} valueSize={22} valueColor={b.echu ? '#B4232A' : undefined} sub={`${b.nb_echues} facture(s) en retard`} /></Grid>
            <Grid item xs={6} md={3}><Kpi compact icon={WarningAmberOutlined} tint={b.PLUS_90 ? 'red' : 'gray'} label="Retard de plus de 90 j" value={F(b.PLUS_90)} valueSize={22} /></Grid>
            <Grid item xs={6} md={3}><Kpi compact icon={ScheduleOutlined} tint={d.actions_du_jour.length ? 'amber' : 'gray'} label="Relances à faire" value={d.actions_du_jour.length} sub="prochaine action atteinte" onClick={() => setVue('factures')} /></Grid>
          </Grid>
          <Panel sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Balance âgée</Typography>
            <Box sx={{ display: 'flex', height: 22, borderRadius: 1, overflow: 'hidden', mb: 1 }}>
              {TRANCHES.map(([k, , c]) => (b[k] > 0 ? <Box key={k} sx={{ width: `${(100 * b[k]) / b.total}%`, bgcolor: c }} title={F(b[k])} /> : null))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {TRANCHES.map(([k, l, c]) => (
                <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: c }} />{l} : <b style={{ fontFamily: tokens.mono }}>{F(b[k])}</b>
                </Box>
              ))}
            </Box>
          </Panel>
        </>
      )}
      <Panel flush>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <ToggleButtonGroup size="small" exclusive value={vue} onChange={(e, v) => v && setVue(v)}>
            <ToggleButton value="clients">Par client</ToggleButton><ToggleButton value="factures">Toutes les factures</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <TableContainer>
          {vue === 'clients' ? (
            <Table size="small">
              <TableHead><TableRow><TableCell /><TableCell>Client</TableCell>{TRANCHES.map(([k, l]) => <TableCell key={k} align="right">{l}</TableCell>)}<TableCell align="right">Total</TableCell></TableRow></TableHead>
              <TableBody>
                {d?.clients.map((c) => (
                  <React.Fragment key={c.client_id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOuverts((o) => ({ ...o, [c.client_id]: !o[c.client_id] }))}>
                      <TableCell sx={{ width: 40 }}><IconButton size="small" aria-label="Détail">{ouverts[c.client_id] ? <ExpandLess /> : <ExpandMore />}</IconButton></TableCell>
                      <TableCell><b>{c.client}</b><Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{c.factures.length} facture(s){c.retard_max > 0 ? ` · retard max ${c.retard_max} j` : ''}</Typography></TableCell>
                      {TRANCHES.map(([k]) => <TableCell key={k} align="right" sx={{ fontFamily: tokens.mono, color: c.balance[k] ? undefined : 'text.disabled' }}>{c.balance[k] ? F(c.balance[k]) : '—'}</TableCell>)}
                      <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 700 }}>{F(c.balance.total)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, borderBottom: ouverts[c.client_id] ? undefined : 'none' }}>
                        <Collapse in={!!ouverts[c.client_id]} unmountOnExit>
                          <Table size="small" sx={{ bgcolor: 'action.hover' }}><TableBody>{c.factures.map((f) => ligneFacture(f, false))}</TableBody></Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {d && !d.clients.length && <TableRow><TableCell colSpan={8} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>Aucune créance : toutes les factures émises sont réglées.</TableCell></TableRow>}
              </TableBody>
            </Table>
          ) : (
            <Table size="small">
              <TableHead><TableRow><TableCell>Facture</TableCell><TableCell>Client</TableCell><TableCell>Date</TableCell><TableCell>Échéance</TableCell><TableCell align="right">Reste dû</TableCell><TableCell>Relance</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>{d?.factures.map((f) => ligneFacture(f))}</TableBody>
            </Table>
          )}
        </TableContainer>
      </Panel>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>
        Factures émises de l'ERP non soldées et factures antérieures à l'ERP restant dues (reprise des bons de commande ; échéance = validation + délai de paiement du BC).
        Le comptable et l'administrateur sont prévenus chaque jour des échéances et des relances prévues, et reçoivent la balance âgée par email le lundi.
      </Typography>
      <RelanceDialog facture={relance} onClose={() => setRelance(null)} onSaved={(m) => { setRelance(null); setInfo(m); charger(); }} />
    </Layout>
  );
};

export default Creances;
